import React, { useState, useEffect, useCallback } from "react";
import {
  Empty,
  Button,
  Space,
  Typography,
  Modal,
  message,
  Tooltip,
  Alert,
} from "antd";
import {
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type { Booking } from "../../types/booking";
import type { Company } from "../../types/company";
import { LeanCustomsIntegrationStripAndModal } from "../LeanCustomsIntegrationStripAndModal";
import CreateLeanCustomsModal from "./CreateLeanCustomsModal";
import { BookingCustomsLeanMatchesPanel } from "./BookingCustomsLeanMatchesPanel";
import { BookingCustomsLeanDeclarationMatchesPanel } from "./BookingCustomsLeanDeclarationMatchesPanel";
import { supabase } from "../../lib/supabase";
import { LeanCustomsService } from "../../services/leancustomsService";
import type { LeanCustomsDeclaration } from "../../services/leancustomsService";
import {
  searchMatchingLeanCustomsDeclarations,
  searchMatchingLeanCustomsOrders,
} from "../../services/leanCustomsBookingSearch";
import {
  bookingHasCommercialInvoice,
  getLeanCustomsReference,
  leanCustomsOrderMatchesBooking,
  type LeanCustomsOrderRefSlice,
} from "../../utils/bookingUtils";
import { LEANCUSTOMS_ORDER_BASE } from "../../constants/leancustoms";
import { BookingCustomsLinkedSummary } from "./BookingCustomsLinkedSummary";

function confirmModalBoolean(options: {
  title: string;
  content: React.ReactNode;
  okText?: string;
}): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: options.title,
      content: options.content,
      okText: options.okText ?? "Continue",
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

function leanOrderIdFromGetResponse(
  data: Record<string, unknown> | undefined,
  fallback: string
): string {
  if (!data) return fallback;
  const id = data.id;
  return typeof id === "string" && id.trim() ? id.trim() : fallback;
}

interface BookingCustomsTabProps {
  booking: Booking;
  onRefresh?: () => void;
}

function resolveCompany(booking: Booking): Company | null {
  const raw = booking.company_info;
  if (!raw) return null;
  const c = Array.isArray(raw) ? raw[0] : raw;
  return c as Company;
}

/**
 * Customs tab: Lean Customs order link, create order, and company integration strip.
 */
export const BookingCustomsTab: React.FC<BookingCustomsTabProps> = ({
  booking,
  onRefresh,
}) => {
  const company = resolveCompany(booking);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [fetchedOrderId, setFetchedOrderId] = useState<string | null>(null);
  const [matchingLcOrders, setMatchingLcOrders] = useState<
    Array<Record<string, unknown> & { id?: string }>
  >([]);
  const [matchingLcDeclarations, setMatchingLcDeclarations] = useState<
    LeanCustomsDeclaration[]
  >([]);
  const [orphanCheckLoading, setOrphanCheckLoading] = useState(false);
  const [linkingOrder, setLinkingOrder] = useState(false);
  const [linkingDeclaration, setLinkingDeclaration] = useState(false);

  useEffect(() => {
    if (booking.leancustoms_order_id) {
      setFetchedOrderId(null);
      return;
    }
    if (!booking.id) return;
    setFetchedOrderId(null);
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("bookings")
        .select("leancustoms_order_id")
        .eq("id", booking.id)
        .single();
      if (cancelled || !data) return;
      setFetchedOrderId((data.leancustoms_order_id as string) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [booking.id, booking.leancustoms_order_id]);

  const orderId = booking.leancustoms_order_id ?? fetchedOrderId ?? null;
  const hasLinkedOrder = !!orderId;
  const hasLinkedDeclaration = !!booking.leancustoms_declaration_id;
  const showOrderMatches = !hasLinkedOrder;
  const showDeclarationMatches = !hasLinkedDeclaration;
  const showLeanMatchSearch = showOrderMatches || showDeclarationMatches;
  const bookingReference = getLeanCustomsReference(booking);
  const companyIdForApi = company?.id ?? booking.company_id;
  const [hasCommercialInvoice, setHasCommercialInvoice] = useState(() =>
    bookingHasCommercialInvoice(booking)
  );

  useEffect(() => {
    setHasCommercialInvoice(bookingHasCommercialInvoice(booking));
  }, [booking.commercial_invoice_file]);

  useEffect(() => {
    if (!booking.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("bookings")
        .select("commercial_invoice_file")
        .eq("id", booking.id)
        .single();
      if (cancelled) return;
      setHasCommercialInvoice(
        bookingHasCommercialInvoice({
          commercial_invoice_file: (data?.commercial_invoice_file as string | null) ?? null,
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [booking.id, booking.commercial_invoice_file]);

  useEffect(() => {
    let cancelled = false;
    const orgId = company?.leancustoms_organization_id;
    const bid = booking.id;

    if (!orgId || !bid || companyIdForApi == null || !showLeanMatchSearch) {
      setMatchingLcOrders([]);
      setMatchingLcDeclarations([]);
      return;
    }

    setOrphanCheckLoading(true);
    (async () => {
      const [orders, declarations] = await Promise.all([
        showOrderMatches
          ? searchMatchingLeanCustomsOrders(
              orgId,
              companyIdForApi,
              bid,
              bookingReference
            )
          : Promise.resolve([]),
        showDeclarationMatches
          ? searchMatchingLeanCustomsDeclarations(
              orgId,
              companyIdForApi,
              bid,
              bookingReference,
              booking.leancustoms_declaration_id,
              orderId
            )
          : Promise.resolve([]),
      ]);
      if (cancelled) return;

      setMatchingLcOrders(orders);
      setMatchingLcDeclarations(declarations);
    })()
      .catch(() => {
        if (!cancelled) {
          setMatchingLcOrders([]);
          setMatchingLcDeclarations([]);
        }
      })
      .finally(() => {
        if (!cancelled) setOrphanCheckLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    company?.leancustoms_organization_id,
    company?.id,
    booking.id,
    booking.company_id,
    booking.leancustoms_declaration_id,
    bookingReference,
    companyIdForApi,
    showLeanMatchSearch,
    showOrderMatches,
    showDeclarationMatches,
    orderId,
    refreshTrigger,
  ]);

  const handleRecheckLeanCustoms = useCallback(() => {
    setRefreshTrigger((t) => t + 1);
  }, []);

  const openCreateLeanCustomsModal = useCallback(() => {
    if (!hasCommercialInvoice) {
      message.warning(
        "Upload a commercial invoice on this booking before creating a Lean Customs order."
      );
      return;
    }
    if (matchingLcOrders.length === 0) {
      setCreateModalOpen(true);
      return;
    }
    Modal.confirm({
      title: "Existing Lean Customs order for this booking",
      icon: <WarningOutlined />,
      width: 520,
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 12 }}>
            We found <strong>{matchingLcOrders.length}</strong> matching order(s) for{" "}
            <Typography.Text code>EFF{booking.id}</Typography.Text>. Use{" "}
            <strong>Link order to booking</strong> below, or continue only if you need a new
            order in Lean Customs.
          </Typography.Paragraph>
          <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflowY: "auto" }}>
            {matchingLcOrders.map((o) => {
              const oid = typeof o.id === "string" ? o.id : "";
              const ref =
                typeof o.reference === "string"
                  ? o.reference
                  : typeof o.booking_number === "string"
                    ? o.booking_number
                    : oid;
              return (
                <li key={oid || ref} style={{ marginBottom: 6 }}>
                  <Typography.Text code style={{ fontSize: 12 }}>
                    {ref}
                  </Typography.Text>
                  {oid ? (
                    <>
                      {" "}
                      <Button
                        type="link"
                        size="small"
                        icon={<LinkOutlined />}
                        href={`${LEANCUSTOMS_ORDER_BASE}/${oid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ padding: 0, height: "auto" }}
                      >
                        Open
                      </Button>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ),
      okText: "Continue to create order",
      cancelText: "Cancel",
      onOk: () => setCreateModalOpen(true),
    });
  }, [hasCommercialInvoice, matchingLcOrders, booking.id]);

  const handleLinkOrder = useCallback(
    async (orderIdToLink: string) => {
      const bid = booking.id;
      if (!bid || companyIdForApi == null) return;

      const rawOrder = orderIdToLink.trim();
      if (!rawOrder) return;

      setLinkingOrder(true);
      try {
        const gor = await LeanCustomsService.getOrder(rawOrder, companyIdForApi);
        if (!gor.success || !gor.data) {
          message.error(gor.error || "Could not load order from Lean Customs.");
          return;
        }
        const nextOrder = leanOrderIdFromGetResponse(gor.data, rawOrder);

        if (!leanCustomsOrderMatchesBooking(gor.data as LeanCustomsOrderRefSlice, bid)) {
          const ok = await confirmModalBoolean({
            title: "Reference does not match this booking",
            content: (
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                This order&apos;s reference does not look like booking{" "}
                <Typography.Text code>EFF{bid}</Typography.Text>. Link only if you are sure.
              </Typography.Paragraph>
            ),
            okText: "Link anyway",
          });
          if (!ok) return;
        }

        const confirmed = await confirmModalBoolean({
          title: "Link order to this booking?",
          content: (
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              Save order <Typography.Text code>{nextOrder}</Typography.Text> on booking{" "}
              <Typography.Text code>EFF{bid}</Typography.Text>?
            </Typography.Paragraph>
          ),
          okText: "Link to booking",
        });
        if (!confirmed) return;

        if (orderId && String(orderId) !== String(nextOrder)) {
          const ok = await confirmModalBoolean({
            title: "Replace linked order?",
            content: (
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                This booking already has order <Typography.Text code>{orderId}</Typography.Text>.
                Replace with <Typography.Text code>{nextOrder}</Typography.Text>?
              </Typography.Paragraph>
            ),
            okText: "Replace order",
          });
          if (!ok) return;
        }

        const { error } = await supabase
          .from("bookings")
          .update({ leancustoms_order_id: nextOrder })
          .eq("id", bid);
        if (error) {
          message.error(error.message || "Failed to update booking");
          return;
        }
        message.success("Lean Customs order linked to this booking.");
        setFetchedOrderId(nextOrder);
        onRefresh?.();
        setRefreshTrigger((t) => t + 1);
      } finally {
        setLinkingOrder(false);
      }
    },
    [booking.id, companyIdForApi, orderId, onRefresh]
  );

  const handleLinkDeclaration = useCallback(
    async (declarationIdToLink: string) => {
      const bid = booking.id;
      if (!bid || companyIdForApi == null) return;

      const rawDeclaration = declarationIdToLink.trim();
      if (!rawDeclaration) return;

      const confirmed = await confirmModalBoolean({
        title: "Link declaration to this booking?",
        content: (
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            Save declaration <Typography.Text code>{rawDeclaration}</Typography.Text> on
            booking <Typography.Text code>EFF{bid}</Typography.Text>? Customers will see
            declaration documents on the booking view when linked.
          </Typography.Paragraph>
        ),
        okText: "Link to booking",
      });
      if (!confirmed) return;

      setLinkingDeclaration(true);
      try {
        const { error } = await supabase
          .from("bookings")
          .update({ leancustoms_declaration_id: rawDeclaration })
          .eq("id", bid);
        if (error) {
          message.error(error.message || "Failed to update booking");
          return;
        }
        message.success("Lean Customs declaration linked to this booking.");
        onRefresh?.();
        setRefreshTrigger((t) => t + 1);
      } finally {
        setLinkingDeclaration(false);
      }
    },
    [booking.id, companyIdForApi, onRefresh]
  );

  if (!company) {
    return (
      <Empty
        description="No company associated with this booking"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  if (!company.leancustoms_organization_id) {
    const companySettingsUrl = `/admin/companies?id=${booking.company_id ?? ""}`;
    return (
      <Empty
        description={
          <span>
            No Lean Customs organisation linked for this company. Link one in{" "}
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => window.open(companySettingsUrl, "_blank")}
              style={{ padding: 0, height: "auto" }}
            >
              Company settings
            </Button>
            .
          </span>
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const integrationCompanyId = company.id ?? booking.company_id;
  if (integrationCompanyId == null) {
    return (
      <Empty
        description="Company id could not be resolved for Lean Customs."
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const companyForIntegration: Company = {
    ...company,
    id: integrationCompanyId,
  };

  return (
    <>
      {showLeanMatchSearch ? (
        <div style={{ marginBottom: 12 }}>
          <Button
            icon={<ReloadOutlined />}
            loading={orphanCheckLoading}
            onClick={handleRecheckLeanCustoms}
          >
            Re-check Lean Customs
          </Button>
        </div>
      ) : null}
      {(hasLinkedOrder || hasLinkedDeclaration) &&
        companyIdForApi != null &&
        company?.leancustoms_organization_id && (
        <BookingCustomsLinkedSummary
          orderId={orderId}
          bookingId={booking.id}
          bookingReference={bookingReference}
          companyId={companyIdForApi}
          organizationId={company.leancustoms_organization_id}
          savedDeclarationId={booking.leancustoms_declaration_id}
          refreshTrigger={refreshTrigger}
          onRefresh={onRefresh}
          onRecheck={handleRecheckLeanCustoms}
        />
      )}
      {showLeanMatchSearch && (
        <Space direction="vertical" size={12} style={{ marginBottom: 16, width: "100%" }}>
          {showOrderMatches ? (
            <BookingCustomsLeanMatchesPanel
              bookingId={booking.id}
              bookingReference={bookingReference}
              orders={matchingLcOrders}
              loading={orphanCheckLoading}
              linking={linkingOrder}
              existingOrderId={orderId}
              onLinkOrder={handleLinkOrder}
            />
          ) : null}
          {showDeclarationMatches ? (
            <BookingCustomsLeanDeclarationMatchesPanel
              bookingId={booking.id}
              bookingReference={bookingReference}
              declarations={matchingLcDeclarations}
              loading={orphanCheckLoading}
              linking={linkingDeclaration}
              existingDeclarationId={booking.leancustoms_declaration_id}
              onLinkDeclaration={handleLinkDeclaration}
            />
          ) : null}
          {!orphanCheckLoading &&
            ((showOrderMatches && matchingLcOrders.length === 0) ||
              (showDeclarationMatches && matchingLcDeclarations.length === 0)) && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {showOrderMatches && matchingLcOrders.length === 0 ? (
                <>
                  No orders in Lean Customs matched{" "}
                  <Typography.Text code>{bookingReference}</Typography.Text> for this company.
                </>
              ) : null}
              {showOrderMatches &&
              matchingLcOrders.length === 0 &&
              showDeclarationMatches &&
              matchingLcDeclarations.length === 0
                ? " "
                : null}
              {showDeclarationMatches && matchingLcDeclarations.length === 0 ? (
                <>
                  No declarations in Lean Customs matched{" "}
                  <Typography.Text code>{bookingReference}</Typography.Text> for this company.
                </>
              ) : null}
              {showOrderMatches && hasCommercialInvoice ? (
                <>
                  {" "}
                  Use <strong>Create order in LeanCustoms</strong> to send this booking as a new
                  order.
                </>
              ) : showOrderMatches && !hasCommercialInvoice ? (
                <>
                  {" "}
                  Upload a commercial invoice on the booking before you can create a Lean Customs
                  order.
                </>
              ) : null}
            </Typography.Paragraph>
          )}
          {showOrderMatches && !hasCommercialInvoice && (
            <Alert
              type="warning"
              showIcon
              message="Commercial invoice required"
              description="Add a commercial invoice to this booking before creating a Lean Customs order."
            />
          )}
          {showOrderMatches ? (
            <Tooltip
              title={
                hasCommercialInvoice
                  ? undefined
                  : "Upload a commercial invoice on this booking first."
              }
            >
              <span>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreateLeanCustomsModal}
                  disabled={!hasCommercialInvoice}
                >
                  Create order in LeanCustoms
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Space>
      )}
      {companyForIntegration.id && companyForIntegration.leancustoms_organization_id && (
        <LeanCustomsIntegrationStripAndModal
          company={companyForIntegration}
          refreshTrigger={refreshTrigger}
        />
      )}
      <CreateLeanCustomsModal
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onSuccess={() => {
          onRefresh?.();
          setRefreshTrigger((t) => t + 1);
        }}
        booking={booking}
        company={company}
      />
    </>
  );
};

export default BookingCustomsTab;
